import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { MdArrowDownward, MdArrowUpward } from "react-icons/md";
import { typeToColor, typeToLabel } from "@/taskpane/utils/filiation";
import CustomCheckbox from "@/taskpane/components/custom-checkbox/CustomCheckbox";
import TableValueCell from "@/taskpane/modules/folder/filiation/filiation-table/TableValueCell";

const PAGINATION_SETTINGS = {
  ROWS_PER_PAGE: 18, // Fixed number of rows per page
  MIN_ROWS_FOR_PAGINATION: 18, // Show pagination only when there are more than 100 rows
  VISIBLE_PAGES: 7 // Number of page numbers to show (excluding first and last)
} as const;

interface TablePaginationActionsProps {
  count: number;
  page: number;
  rowsPerPage: number;
  onPageChange: (event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => void;
}

function TablePaginationActions(props: TablePaginationActionsProps) {
  const { count, page, rowsPerPage, onPageChange } = props;
  const totalPages = Math.ceil(count / rowsPerPage);

  // Generate the array of page numbers to display
  const getPageNumbers = () => {
    const pageNumbers: (number | string)[] = [];
    const halfVisible = Math.floor(PAGINATION_SETTINGS.VISIBLE_PAGES / 2);
    
    // Always show first page
    pageNumbers.push(0);
    
    let startPage = Math.max(1, page - halfVisible);
    let endPage = Math.min(totalPages - 2, page + halfVisible);
    
    // Adjust if we're near the start
    if (page <= halfVisible) {
      endPage = Math.min(PAGINATION_SETTINGS.VISIBLE_PAGES, totalPages - 2);
    }
    
    // Adjust if we're near the end
    if (page >= totalPages - halfVisible - 1) {
      startPage = Math.max(1, totalPages - PAGINATION_SETTINGS.VISIBLE_PAGES - 1);
    }
    
    // Add ellipsis after first page if needed
    if (startPage > 1) {
      pageNumbers.push('...');
    }
    
    // Add middle pages
    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }
    
    // Add ellipsis before last page if needed
    if (endPage < totalPages - 2) {
      pageNumbers.push('...');
    }
    
    // Always show last page if there is more than one page
    if (totalPages > 1) {
      pageNumbers.push(totalPages - 1);
    }
    
    return pageNumbers;
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      {getPageNumbers().map((pageNum, index) => 
        typeof pageNum === 'number' ? (
          <Button
            key={index}
            onClick={(e) => onPageChange(e, pageNum)}
            variant={page === pageNum ? "contained" : "text"}
            size="small"
            sx={{
              minWidth: '24px',
              height: '24px',
              p: 0,
              borderRadius: '12px',
              fontSize: '0.75rem',
              '&.MuiButton-contained': {
                boxShadow: 'none'
              }
            }}
          >
            {pageNum + 1}
          </Button>
        ) : (
          <Typography key={index} sx={{ color: 'text.secondary', fontSize: '0.75rem', mx: 0.25 }}>
            {pageNum}
          </Typography>
        )
      )}
    </Box>
  );
}

import {
  Box,
  Button,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
  useTheme,
} from "@mui/material";

import { sortNumberOrString } from "@/taskpane/utils/filiation";
import { useWindowSize } from "@uidotdev/usehooks";

import type { FiliationTableProps } from "./FiliationTable.types";


export default function FiliationTable({
  blockUi,
  colNum,
  data,
  filters,
  loading,
  noDataMessage,
  onSelectProperty,
  selectedProperties,
  type,
}: FiliationTableProps) {
  const [sort, setSort] = useState<"asc" | "desc">("asc");
  const [page, setPage] = useState(0);
  const rowsPerPage = PAGINATION_SETTINGS.ROWS_PER_PAGE;

  const theme = useTheme();

  const scrollRef = useRef<HTMLTableSectionElement>(null);

  
  /**
   * @description Handle the sort of the filiation table
   *
   * @callback
   * @dependency {string} sort - Current sort state. Is either "asc" or "desc"
   * @dependency {function} sort - setState function for the sort state
   */
  const handleSort = useCallback(() => {
    setSort((prev) => {
      if (prev === "asc") {
        return "desc";
      } else {
        return "asc";
      }
    });
  }, [sort, setSort]);

  /**
   * @description Filter the data based on the search input and the selected parcelles and volumes.
   *
   * @memoization
   * @dependency {PropertyNode[]} data - The data to filter.
   * @dependency {Object} filters - The filters object containing the search input, the selected parcelles and volumes.
   */
  const filteredData = useMemo(() => {
    let filtered = data ?? [];

    if (filters.search) {
      filtered = filtered.filter(
        (node) =>
          (node?.value ?? '').toLowerCase().includes(filters.search.toLowerCase()) ||
          (node?.parents ?? []).some((parent) => (parent?.value ?? '').toLowerCase().includes(filters.search.toLowerCase()))
      );
    }

    if (filters.parcelleIds.length > 0) {
      filtered = filtered.filter((node) => (node?.parents ?? []).some((parent) => parent?.id && filters.parcelleIds.includes(parent.id)));
    }

    if (filters.volumeIds.length > 0) {
      filtered = filtered.filter((node) => (node?.parents ?? []).some((parent) => parent?.id && filters.volumeIds.includes(parent.id)));
    }

    return filtered;
  }, [data, filters]);

  /**
   * @description Sort the filtered data based on the sort state.
   *
   * @memoization
   * @dependency {PropertyNode[]} filteredData - The filtered data to sort.
   * @dependency {string} sort - The sort state. Is either "asc" or "desc"
   */
  const filteredAndSortedData = useMemo(() => {
    return sortNumberOrString(filteredData, sort);
  }, [filteredData, sort]);

  const paginatedData = useMemo(() => {
    return filteredAndSortedData.slice(page * rowsPerPage, (page + 1) * rowsPerPage);
  }, [filteredAndSortedData, page, rowsPerPage]);

  const handleChangePage = useCallback((_: unknown, newPage: number) => {
    setPage(newPage);
  }, []);

  return (
    <Stack 
      spacing={2}
      sx={{ 
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        paddingTop: "4px",
        pb: filteredAndSortedData?.length === 0 ? 0 : 2,
      }}
    >
      <Box sx={{ flexGrow: 1, minHeight: 0 }}>
        <TableContainer
          sx={{ 
            height: '100%',
            overflowY: "auto",
            pb: 0, // Extra padding at bottom to prevent sticky buttons from covering last items
          }}
        >
        {/* Table */}
        <Table stickyHeader>

          {/* Table Head */}
          <TableHead>
            <TableRow>

              {/* Checkbox column header */}
              <TableCell width={5}>
                <CustomCheckbox
                  sx={{ p: 0 }}
                  disabled={!!blockUi}
                  color={typeToColor(type)}
                  checked={filteredAndSortedData.every((node) => selectedProperties.includes(node.id)) && filteredAndSortedData.length > 0}
                  onChange={(_, checked) =>
                    onSelectProperty(
                      filteredAndSortedData.map((node) => node.id),
                      checked
                    )
                  }
                />
              </TableCell>

              {/* Sort Icon column header */}
              <TableCell onClick={handleSort} style={{ cursor: "pointer" }}>
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <Typography fontSize={12} color={`${typeToColor(type)}.600`} fontWeight={600}>
                    {typeToLabel(type)}
                  </Typography>
                  {sort === "asc" ? (
                    <MdArrowDownward size={16} style={{ color: theme.palette[typeToColor(type) || "primary"].main }} />
                  ) : (
                    <MdArrowUpward size={16} style={{ color: theme.palette[typeToColor(type) || "primary"].main }} />
                  )}
                </Stack>
              </TableCell>

              {/* Lot column header */}
              {type === "lot" && (
                <TableCell>
                  <Typography fontStyle="italic" fontSize={12} color={`${typeToColor("volume")}.600`}>
                    Volume attaché
                  </Typography>
                </TableCell>
              )}

              {/* Lot/Volume column header */}
              {(type === "lot" || type === "volume") && (
                <TableCell>
                  <Typography fontStyle="italic" fontSize={12} color={`${typeToColor("cadastre")}.600`}>
                    Parcelle attachée
                  </Typography>
                </TableCell>
              )}
            </TableRow>
          </TableHead>

          {/* Table Body */}
          <TableBody
          ref={scrollRef}>
            {!loading &&
              filteredAndSortedData.length > 0 &&
              paginatedData.map((node) => (
                  <TableRow
                  key={node.id}
                  >

                    <TableCell width={5}>
                      <CustomCheckbox
                        disabled={!!blockUi}
                        sx={{ py: 0.2, px: 0 }}
                        color={typeToColor(type)}
                        checked={selectedProperties.includes(node.id)} // As the selectedProperties is updated with filiation element that are chosen, it carry the actual checked the value.
                        onChange={(_, checked) => onSelectProperty([node.id], checked)}
                      />
                    </TableCell>

                    <TableCell>
                      <Typography fontSize={12} color={`${typeToColor(type)}.600`} fontWeight={600}>
                        {node.value}
                      </Typography>
                    </TableCell>

                    {type === "lot" && <TableValueCell node={node} color={typeToColor("volume")} type="volume" />}

                    {(type === "lot" || type === "volume") && (
                      <TableValueCell node={node} color={typeToColor("cadastre")} type="cadastre" />
                    )}
                  </TableRow>
                ))}

            {loading && <TableRowsLoader rowsNum={6} colNum={colNum} />}

          </TableBody>

        </Table>
        </TableContainer>
      </Box>

      {/* Display a message when there is no data */}
      {!loading && filteredData.length === 0 && (
        <Typography fontSize={12} color="gray" textAlign="center">
          {noDataMessage}
        </Typography>
      )}

      {/* Pagination */}
      {!loading && filteredAndSortedData.length > PAGINATION_SETTINGS.MIN_ROWS_FOR_PAGINATION && (
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'center',
          flexShrink: 0,
          bgcolor: 'background.paper',
          borderTop: 1,
          borderColor: 'divider',
          py: 0.5,
          pb: 5
        }}>
          <TablePaginationActions
            count={filteredAndSortedData.length}
            page={page}
            rowsPerPage={rowsPerPage}
            onPageChange={handleChangePage}
          />
        </Box>
      )}
    </Stack>
  );
}

/**
 * @description Skeleton loader for the table rows
 *
 * @param {number} rowsNum - Number of rows to display
 * @param {number} colNum - Number of columns to display
 */
const TableRowsLoader = ({ rowsNum = 5, colNum = 3 }) => {
  return [...Array(rowsNum)].map((_, index) => (
    <TableRow key={index}>
      <TableCell component="th" scope="row">
        <Skeleton animation="wave" variant="text" />
      </TableCell>
      <>
        {colNum > 0 &&
          [...Array(colNum)].map((_, index) => (
            <TableCell key={`col-${index}`}>
              <Skeleton animation="wave" variant="text" />
            </TableCell>
          ))}
      </>
    </TableRow>
  ));
};
